import api from 'services/api';
import { BookingProps } from './booking-service';
import { BuildingProps } from './building-service';
import ReactGA from 'react-ga4';

/* The MobileUserResource type represents occupants associated with a Resource, including information about permissions, organization, 
and occupant name. */
export type MobileUserResource = {
  resourceUuid: string;
  mobileUserUuid: string;
  permission: boolean | any;
  organizationUuid: string;
  organizationName: string;
  occupantName: string;
};

/* The ResourceProps type represents the properties of a Resource, including its UUID, name, type, description, image, open, close,  
buildingUuid, bookings, and mobileUsers. */
export type ResourceProps = {
  uuid?: string;
  name: string;
  type: string;
  description: string;
  image: any;
  open: string | any;
  close: string | any;
  buildingUuid: any;
  building?: BuildingProps;
  bookings?: BookingProps[];
  mobileUsers?: MobileUserResource[];
  spaceUuid?: string;
};

// Define the resource types
const resourceTypes = [
  'MEETING ROOM',
  'DESK',
  'PHONE BOOTH',
  'BOARDROOM',
  'ROOM',
  'COMMON AREA',
  'OFFICE',
  'LAB'
];

// Create resourceCategories by mapping over the resource types
export const resourceCategories: ResourceProps[] = resourceTypes.map((type) => ({
  uuid: type,
  name: `All ${type.charAt(0).toUpperCase()}${type.slice(1).toLowerCase()}s`,
  type: type,
  description: `${type} description`,
  image: '',
  open: '',
  close: '',
  buildingUuid: ''
}));

/* The function `findAllResources` is an asynchronous function that makes a GET request to the `/apiv1/resources/` endpoint and returns 
all the Resources data from the response. */
const findAllResources = async () => {
  try {
    const response = await api.get<ResourceProps[]>('/apiv1/resources/');
    return response?.data ?? []; // Return an empty array if data is null
  } catch (error) {
    console.error('Error fetching resources');
    return []; // Return an empty array in case of an error
  }
};

/* The function `findResourceById` retrieves a Resource object from an API based on the provided UUID. */
const findResourceById = async (uuid: string | undefined) => {
  const response = await api.get<ResourceProps>(`/apiv1/resource/${uuid}/`);
  return response?.data;
};

/* The function creates a new Resource by making a POST request to the `/apiv1/resource/` endpoint with the provided Resource data. */
const createResource = async (newResource: ResourceProps) => {
  ReactGA.event({
    category: 'User',
    action: 'Create Amenity',
    label: `Name: ${newResource.name}`
  });

  const formData = new FormData();
  formData.append('name', newResource.name);
  formData.append('type', newResource.type);
  formData.append('description', newResource.description);
  formData.append('open', newResource.open);
  formData.append('close', newResource.close);
  formData.append('buildingUuid', newResource.buildingUuid);
  formData.append('image', newResource.image);
  if (newResource.spaceUuid) {
    formData.append('spaceUuid', newResource.spaceUuid);
  }

  const response = await api.post<ResourceProps>('/apiv1/resource/', formData);
  return response?.data;
};

/* The function `updateResource` is an asynchronous function that updates a Resource using a PUT request to an API endpoint. */
const updateResource = async (uuid: string, updatedResource: ResourceProps) => {
  ReactGA.event({
    category: 'User',
    action: 'Update Amenity',
    label: `Uuid: ${uuid}, Name: ${updatedResource.name}`
  });

  const formData = new FormData();
  formData.append('uuid', uuid);
  formData.append('name', updatedResource.name);
  formData.append('type', updatedResource.type);
  formData.append('description', updatedResource.description);
  formData.append('open', updatedResource.open);
  formData.append('close', updatedResource.close);
  formData.append('buildingUuid', updatedResource.buildingUuid);
  formData.append('image', updatedResource.image);
  if (updatedResource.spaceUuid) {
    formData.append('spaceUuid', updatedResource.spaceUuid);
  }

  const response = await api.put<ResourceProps>(`/apiv1/resource/${uuid}/`, formData);
  return response?.data;
};

/* The function `updateManageResource` is an asynchronous function that updates the permission of a Resource for a mobile user using a PUT request to an API endpoint. */
const updateManageResource = async (
  resourceUuid: string,
  updatedResourcePermission: MobileUserResource
) => {
  ReactGA.event({
    category: 'User',
    action: 'Update Manage Amenity',
    label: `Resource Uuid: ${resourceUuid}`
  });

  const response = await api.put<MobileUserResource>(
    `/apiv1/resource/${resourceUuid}/occupants`,
    updatedResourcePermission
  );
  return response?.data;
};

/* The function `deleteResourceById` is an asynchronous function that deletes a Resource by its UUID using an API call. */
const deleteResourceById = async (uuid: string | undefined) => {
  const response = await api.delete<ResourceProps>(`/apiv1/resource/${uuid}/`);
  return response?.data;
};

const ResourceService = {
  findAllResources,
  findResourceById,
  createResource,
  updateResource,
  updateManageResource,
  deleteResourceById
};

export default ResourceService;
